/**
 * Pure filter/display logic for the ActivityFeed component — category-chip + time-window
 * filtering, empty-state selection, actor-label rendering. Split out from ActivityFeed.tsx so
 * that file exports only the component (react-refresh/only-export-components) and so this logic
 * is directly unit-testable without a DOM.
 */
import type { NormalizedActivityRow } from "@/components/control-tower/activity-feed";
import {
  CATEGORIES_WITHOUT_KINDS,
  type ActivityActor,
  type ActivityCategory,
} from "@/lib/api/activity";

export type TimeWindow = "24h" | "7d" | "30d" | "all";

export const TIME_WINDOWS: TimeWindow[] = ["24h", "7d", "30d", "all"];

const TIME_WINDOW_MS: Record<Exclude<TimeWindow, "all">, number> = {
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
};

export const TIME_WINDOW_LABEL_FI: Record<TimeWindow, string> = {
  "24h": "24 h",
  "7d": "7 vrk",
  "30d": "30 vrk",
  all: "Kaikki",
};

export function withinTimeWindow(
  when: string,
  window: TimeWindow,
  now: number,
): boolean {
  if (window === "all") return true;
  const t = Date.parse(when);
  if (Number.isNaN(t)) return false;
  return now - t <= TIME_WINDOW_MS[window];
}

// Category chips and the time window compose as AND; a multi-category row matches the chip
// filter as soon as any one of its categories is active.
export function filterRows(
  rows: NormalizedActivityRow[],
  activeCategories: ReadonlySet<ActivityCategory>,
  window: TimeWindow,
  now: number,
): NormalizedActivityRow[] {
  return rows.filter(
    (row) =>
      row.category.some((c) => activeCategories.has(c)) &&
      withinTimeWindow(row.when, window, now),
  );
}

export type ActivityEmptyState =
  "no-activity" | "no-match" | "not-in-feed-yet" | null;

// Toiminta selection is entirely kind-less (`CATEGORIES_WITHOUT_KINDS`, today only Strategia, §2.2)
// — such a category carries no event kind by design, so zero rows here means "not shipped yet",
// not "no matches". Empty selection (no chips active) is not this case — that is a plain no-match.
export function isKindlessOnlySelection(
  activeCategories: ReadonlySet<ActivityCategory>,
): boolean {
  if (activeCategories.size === 0) return false;
  return [...activeCategories].every((category) =>
    CATEGORIES_WITHOUT_KINDS.includes(category),
  );
}

// Distinguishes "nothing happened in this scope", "something happened, but the active filter
// hides it", and "this Toiminta selection has no v1 event kind at all" — the three Finnish empty
// states the acceptance criteria calls for. The kind-less case takes precedence: it is a property
// of the chosen filter, not of what rows happen to exist.
export function emptyStateFor(
  totalRows: number,
  filteredRows: number,
  activeCategoriesAreKindless: boolean,
): ActivityEmptyState {
  if (activeCategoriesAreKindless) return "not-in-feed-yet";
  if (totalRows === 0) return "no-activity";
  if (filteredRows === 0) return "no-match";
  return null;
}

// Final top-N slice, applied after the merged when-descending sort and any category/time
// filtering — never re-sorts, just caps how many rows a strip embed shows.
export function limitRows(
  rows: NormalizedActivityRow[],
  limit?: number,
): NormalizedActivityRow[] {
  return limit === undefined ? rows : rows.slice(0, limit);
}

export interface FeedChrome {
  showCategoryChips: boolean;
  showTimeWindowControl: boolean;
  showViewAllLink: boolean;
}

// Compact/strip mode (a lens embed, e.g. story #864) hides the interactive controls — nothing to
// toggle when the feed is scoped to one fixed category — and shows a "see everything" link out to
// the full Tapahtumaloki page instead.
export function feedChromeFor(compact: boolean): FeedChrome {
  return {
    showCategoryChips: !compact,
    showTimeWindowControl: !compact,
    showViewAllLink: compact,
  };
}

export interface ActorDisplay {
  label: string;
  isHuman: boolean;
}

// Actor visibility (human vs Kairos-originated) is never optional (§2.2) — every row's `who`
// resolves to a distinct label/icon pairing. `label` is already the resolved proper noun from
// the backend (a person's identity, or the named Kairos agent); the generic Finnish fallbacks
// here only cover the `null`-label cases the backend documents (an unnamed agent kind, `system`).
export function actorDisplay(who: ActivityActor): ActorDisplay {
  if (who.kind === "system") return { label: "Järjestelmä", isHuman: false };
  if (who.kind === "agent")
    return { label: who.label ?? "Kairos-agentti", isHuman: false };
  return { label: who.label ?? "Käyttäjä", isHuman: true };
}

export function relativeTimeFi(iso: string, now: number): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const diffMin = Math.floor((now - t) / 60_000);
  if (diffMin < 1) return "juuri nyt";
  if (diffMin < 60) return `${diffMin} min sitten`;
  const h = Math.floor(diffMin / 60);
  if (h < 24) return `${h} h sitten`;
  const d = Math.floor(h / 24);
  return d === 1 ? "eilen" : `${d} vrk sitten`;
}

// Subline fact order shared by both ActivityFeed chrome modes: who · [building — multi-building
// scope only] · [entity — when the row is not building-level] · relative time. `actorLabel` is
// nullable so a caller whose rows are already known to share one actor (AgentTeamPanel's detail
// panel — every row is that one agent) can omit it instead of re-deriving the same composition.
export function subline(
  row: NormalizedActivityRow,
  now: number,
  actorLabel: string | null,
  showBuildingName: boolean,
): string {
  return [
    actorLabel,
    showBuildingName ? row.buildingName : null,
    row.where.label,
    relativeTimeFi(row.when, now),
  ]
    .filter((fact): fact is string => Boolean(fact))
    .join(" · ");
}
